'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import type {
  PaginatedSavedSearchDto,
  SavedSearchDto,
  SavedSearchResponseDto,
} from '@b-stock/saved-search-api-client'
import type { AllListingsFilters } from '@b-stock/search-api-client'

import {
  type ListingFiltersState,
  formatFilters,
} from '@/components/listings/search/ListingSearchProvider'
import { redirectToAuth } from '@/helpers/auth'
import { PermissionGranule, usePermission } from '@/helpers/permissions'
import {
  useCreateSavedSearch,
  findAllQuery,
  findByIdQuery,
  useUpdateById,
} from '@/queries/savedSearchQuery'

import useSavedSearchId from './useSavedSearchId'
import filtersToStrings from './utils'

export type SavedSearchContext = {
  createSavedSearch: (filters: ListingFiltersState) => Promise<void>
  currentSavedSearch: SavedSearchResponseDto | null
  currentUsersCurrentSavedSearch: SavedSearchResponseDto | null
  currentUsersCurrentSavedSearchName?: string
  deleteSavedSearch: (id: string) => Promise<void>
  hasValidCurrentUserSavedSearch: boolean
  isCreating: boolean
  isFetching: boolean
  isUpdating: boolean
  savedSearches: SavedSearchDto[]
  savedSearchesCount: number
  savedSearchId: string | null
  justSaved: boolean
  showTooltip: boolean
  toggleTooltip: (value: boolean) => void
  updateSavedSearchName: (id: string, newName: string) => Promise<void>
}

const noopAsync = async () => {}
const noop = () => {}

const defaultContext: SavedSearchContext = {
  createSavedSearch: noopAsync,
  currentSavedSearch: null,
  currentUsersCurrentSavedSearch: null,
  deleteSavedSearch: noopAsync,
  hasValidCurrentUserSavedSearch: false,
  isCreating: false,
  isFetching: true,
  isUpdating: false,
  savedSearches: [],
  savedSearchesCount: 0,
  savedSearchId: null,
  justSaved: false,
  showTooltip: false,
  toggleTooltip: noop,
  updateSavedSearchName: noopAsync,
}

const SavedSearchContext = createContext<SavedSearchContext>(defaultContext)

export const useSavedSearch = () => {
  return useContext(SavedSearchContext)
}

type SavedSearchProvider = {
  allFilters: AllListingsFilters
  children: React.ReactNode
}

export default function SavedSearchProvider({
  allFilters,
  children,
}: SavedSearchProvider) {
  const [optimisticallyValid, setOptimisticallyValid] = useState(false)
  const [hasValidCurrentUserSavedSearch, setHasValidCurrentUserSavedSearch] =
    useState(false)
  const [savedSearches, setSavedSearches] = useState<SavedSearchDto[]>([])
  const [currentSavedSearch, setCurrentSavedSearch] =
    useState<SavedSearchResponseDto | null>(null)
  const [currentUsersCurrentSavedSearch, setCurrentUsersCurrentSavedSearch] =
    useState<SavedSearchResponseDto | null>(null)
  const [
    currentUsersCurrentSavedSearchName,
    setCurrentUsersCurrentSavedSearchName,
  ] = useState('')
  const [savedSearchesCount, setSavedSearchesCount] = useState(0)
  const [isCreating, setIsCreating] = useState(false)
  const [isFetching, setIsFetching] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [justSaved, setJustSaved] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  const decodedAccessToken = useDecodedAccessToken()
  const { accountId, personId } = decodedAccessToken ?? {}
  const queryClient = useQueryClient()

  const createSavedSearchMutation = useCreateSavedSearch()
  const updateByIdMutation = useUpdateById()

  const { savedSearchId, setSavedSearchId } = useSavedSearchId()

  const canReadSavedSearches = usePermission(
    PermissionGranule.FEBP_SAVED_SEARCHES_READ
  )

  // only fetch when the user has saved search read permission
  const { data, isFetching: isFetchingAll } = findAllQuery.useWithOpts(
    [
      {
        accountId,
        personId,
        limit: 100,
      },
    ],
    {
      enabled: canReadSavedSearches,
    }
  )

  // fetch regardless of user being logged in (but require FF)
  const { data: foundSavedSearch } = findByIdQuery.useWithOpts(
    [{ id: savedSearchId ?? '' }],
    { enabled: !!savedSearchId }
  )

  useEffect(() => {
    setSavedSearches(data?.savedSearches || []) // oxlint-disable-line react/set-state-in-effect
    setIsFetching(isFetchingAll)
    setSavedSearchesCount(data?.total || 0)
  }, [data, isFetchingAll])

  useEffect(() => {
    if (savedSearchId) {
      // finding result among savedSearches is faster, but fall back to findById result
      const savedSearch = savedSearches.find(({ _id }) => _id === savedSearchId)
      setCurrentSavedSearch(savedSearch || foundSavedSearch || null) // oxlint-disable-line react/set-state-in-effect

      if (accountId && personId && savedSearch) {
        setCurrentUsersCurrentSavedSearch(savedSearch)
      } else {
        setCurrentUsersCurrentSavedSearch(null)
      }
    } else {
      setCurrentSavedSearch(null)
      setCurrentUsersCurrentSavedSearch(null)
    }
  }, [foundSavedSearch, savedSearchId, savedSearches, accountId, personId])

  useEffect(() => {
    const validSavedSearch = savedSearches.find(
      ({ _id }) => _id === savedSearchId
    )
    const isValidSearchId = savedSearchId && !!validSavedSearch

    if (!optimisticallyValid) {
      const _hasValidCurrentUserSavedSearch =
        !!accountId && !!personId && !!isValidSearchId
      // oxlint-disable-next-line react/set-state-in-effect
      setHasValidCurrentUserSavedSearch(
        !!accountId && !!personId && !!isValidSearchId
      )

      setCurrentUsersCurrentSavedSearchName(
        _hasValidCurrentUserSavedSearch ? validSavedSearch.name : ''
      )
    }

    if (isValidSearchId) setOptimisticallyValid(false)
  }, [
    accountId,
    currentSavedSearch,
    optimisticallyValid,
    personId,
    savedSearchId,
    savedSearches,
  ])

  const toggleTooltip = useCallback(
    (value: boolean) => setShowTooltip(value),
    []
  )

  useEffect(() => {
    if (!justSaved) return
    const timeout = setTimeout(() => setJustSaved(false), 2_000)
    return () => clearTimeout(timeout)
  }, [justSaved])

  const createSavedSearch = async (filters: ListingFiltersState) => {
    if (!accountId || !personId) {
      redirectToAuth()
      return
    }

    setIsCreating(true)

    // endTimeRange is deep-link only — a fixed absolute hour that has no
    // meaning in an ongoing notification-driven saved search — so blank it
    // before persisting either the API filters or the display chip label.
    const persistedFilters: ListingFiltersState = {
      ...filters,
      endTimeRange: [null, null],
    }

    const displayFilters = filtersToStrings(persistedFilters, allFilters)
    const name = displayFilters.join(', ')

    try {
      const { savedSearch, status, statusText } =
        await createSavedSearchMutation.mutateAsync({
          personId,
          accountId,
          name,
          filters: formatFilters(persistedFilters),
          displayFilters,
          emailsEnabled: true,
        })

      if (status !== 201) {
        throw new Error(statusText)
      }

      // sync state with updated data
      setSavedSearches((prev) => [savedSearch, ...prev])
      setSavedSearchesCount((prev) => prev + 1)

      // set the id for the URL which then applies the filters
      setSavedSearchId(savedSearch._id)

      // optimistically set state
      setOptimisticallyValid(true)
      setHasValidCurrentUserSavedSearch(true)
      setCurrentUsersCurrentSavedSearchName(name)
      toggleTooltip(true)
      setJustSaved(true)

      // Invalidate cache to refresh other components using findAllQuery
      void queryClient.invalidateQueries({ queryKey: [findAllQuery.name] })
    } catch (error) {
      console.error('Error creating saved search', error)
    } finally {
      setIsCreating(false)
    }
  }

  const updateSavedSearchName = async (id: string, newName: string) => {
    setIsUpdating(true)

    try {
      const { savedSearch, status, statusText } =
        await updateByIdMutation.mutateAsync({
          id,
          updateSavedSearchDto: {
            name: newName,
          },
        })

      if (status !== 200) {
        throw new Error(statusText)
      }

      setSavedSearches((prev) =>
        prev.map((ss) => (ss._id === savedSearch._id ? savedSearch : ss))
      )

      // Invalidate cache to refresh other components using findAllQuery
      void queryClient.invalidateQueries({ queryKey: [findAllQuery.name] })
    } catch (error) {
      console.error('Error updating name', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const deleteSavedSearch = async (id: string) => {
    setIsUpdating(true)

    try {
      await updateByIdMutation.mutateAsync({
        id,
        updateSavedSearchDto: {
          isActive: false,
        },
      })

      setSavedSearches((prev) => prev.filter(({ _id }) => _id !== id))
      setSavedSearchesCount((prev) => prev - 1)

      // Keep cache in sync to avoid stale data briefly showing the deleted item during refetch
      queryClient.setQueriesData<PaginatedSavedSearchDto>(
        { queryKey: [findAllQuery.name] },
        (oldData) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            savedSearches: oldData.savedSearches.filter(
              ({ _id }) => _id !== id
            ),
            total: oldData.total - 1,
          }
        }
      )

      // Invalidate cache to refresh other components using findAllQuery
      void queryClient.invalidateQueries({ queryKey: [findAllQuery.name] })
    } catch (error) {
      console.error('Error deleting saved search', error)
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <SavedSearchContext.Provider
      value={{
        createSavedSearch,
        currentSavedSearch,
        currentUsersCurrentSavedSearch,
        currentUsersCurrentSavedSearchName,
        deleteSavedSearch,
        hasValidCurrentUserSavedSearch,
        isCreating,
        isFetching,
        isUpdating,
        justSaved,
        savedSearches,
        savedSearchesCount,
        savedSearchId,
        showTooltip,
        toggleTooltip,
        updateSavedSearchName,
      }}
    >
      {children}
    </SavedSearchContext.Provider>
  )
}
